import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 23: UTSAV
 * Style: Festive Indian Luxury
 * Best for: Indian wedding wear, festive ethnic fashion, occasion-based collections
 * Emerald/gold jewel tones with occasion-based navigation
 */
const Template23 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [occasionScrollLeft, setOccasionScrollLeft] = useState(0);
    const touchStartX = useRef(0);
    const occasionScrollRef = useRef(null);

    // Occasions for the Carousel — THE KILLER FEATURE
    const occasions = [
        { name: 'Wedding', image: 'https://images.pexels.com/photos/8881954/pexels-photo-8881954.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#7A1B3B' },
        { name: 'Sangeet', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#6B2FA0' },
        { name: 'Haldi', image: 'https://images.pexels.com/photos/30706032/pexels-photo-30706032.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#C5974B' },
        { name: 'Mehendi', image: 'https://images.pexels.com/photos/3014856/pexels-photo-3014856.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#2D7A4F' },
        { name: 'Reception', image: 'https://images.pexels.com/photos/12995512/pexels-photo-12995512.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#1B3A6D' },
        { name: 'Diwali', image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#C5974B' },
        { name: 'Eid', image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#1B3A2D' },
        { name: 'Puja', image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=600', color: '#8B2500' },
    ];

    // Trending Collections
    const trendingCollections = [
        {
            name: 'The Bridal Edit',
            description: 'Timeless bridal ensembles woven with heritage and adorned in gold.',
            occasion: 'Wedding',
            image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
        {
            name: 'Sangeet Night',
            description: 'Dance the night away in bold colors and sparkling silhouettes.',
            occasion: 'Sangeet',
            image: 'https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
        {
            name: 'Festive Luxe',
            description: 'Rich fabrics and intricate details for every celebration.',
            occasion: 'Festive',
            image: 'https://images.pexels.com/photos/17570989/pexels-photo-17570989.jpeg?auto=compress&cs=tinysrgb&w=1920',
        },
    ];

    // Products
    const products = [
        { id: 1, name: 'Bridal Silk Lehenga', price: '\u20B945,999', occasion: 'Wedding', image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 2, name: 'Sangeet Anarkali Set', price: '\u20B918,500', occasion: 'Sangeet', image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 3, name: 'Festive Zari Saree', price: '\u20B912,999', occasion: 'Diwali', image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 4, name: 'Wedding Sherwani', price: '\u20B928,000', occasion: 'Wedding', image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Mehendi Sharara Set', price: '\u20B99,999', occasion: 'Mehendi', image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Reception Gown', price: '\u20B922,500', occasion: 'Reception', image: 'https://images.pexels.com/photos/9901808/pexels-photo-9901808.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 7, name: 'Diwali Kurta Set', price: '\u20B96,800', occasion: 'Diwali', image: 'https://images.pexels.com/photos/17611651/pexels-photo-17611651.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 8, name: 'Eid Special Gharara', price: '\u20B914,999', occasion: 'Eid', image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=800' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The bridal lehenga was a dream come true. Every stitch spoke of heritage and craftsmanship. I felt like royalty on my special day.',
            name: 'Priya Sharma',
            city: 'Mumbai',
            occasion: 'Wore for her wedding',
            rating: 5,
        },
        {
            text: 'Wore the Anarkali set to my sister\'s sangeet and received so many compliments! The fabric quality and zari work are simply outstanding.',
            name: 'Ananya Kapoor',
            city: 'Delhi',
            occasion: 'Wore to sister\'s sangeet',
            rating: 5,
        },
        {
            text: 'Ordered a custom sherwani for Diwali. The fit was perfect and the antique gold embroidery was more beautiful than I imagined. Will be ordering again for the wedding season!',
            name: 'Rajesh Malhotra',
            city: 'Jaipur',
            occasion: 'Wore for Diwali celebration',
            rating: 5,
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    // Occasion carousel scroll
    const scrollOccasions = (direction) => {
        if (occasionScrollRef.current) {
            const scrollAmount = 240;
            const newScroll = direction === 'left'
                ? occasionScrollRef.current.scrollLeft - scrollAmount
                : occasionScrollRef.current.scrollLeft + scrollAmount;
            occasionScrollRef.current.scrollTo({ left: newScroll, behavior: 'smooth' });
        }
    };

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        if (email.trim()) {
            setEmail('');
        }
    };

    return (
        <div style={{ fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');
                @keyframes utsavShimmer {
                    0% { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes utsavFloat {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-8px); }
                }
                @keyframes utsavFadeUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .utsav-occasion-scroll::-webkit-scrollbar { display: none; }
                .utsav-occasion-scroll { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* ===== SECTION 1: GRAND HERO ===== */}
            <section className="relative w-full overflow-hidden" style={{ minHeight: '100vh', backgroundColor: '#FFFDF8' }}>
                {/* Hero Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/8881954/pexels-photo-8881954.jpeg?auto=compress&cs=tinysrgb&w=1920"
                        alt="Festive Indian Fashion"
                        className="w-full h-full object-cover"
                    />
                    {/* Emerald Gradient from Bottom */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(to top, rgba(27, 58, 45, 0.95) 0%, rgba(27, 58, 45, 0.6) 35%, rgba(27, 58, 45, 0.2) 60%, transparent 100%)'
                        }}
                    />
                    {/* Subtle Gold Shimmer Overlay */}
                    <div
                        className="absolute inset-0"
                        style={{
                            background: 'linear-gradient(135deg, transparent 40%, rgba(197, 151, 75, 0.08) 50%, transparent 60%)',
                            backgroundSize: '200% 200%',
                            animation: 'utsavShimmer 6s linear infinite',
                        }}
                    />
                </div>

                {/* Hero Content */}
                <div className="relative z-10 flex flex-col items-center justify-end min-h-screen pb-20 sm:pb-28 px-4 sm:px-6 text-center">
                    <p
                        className="text-xs sm:text-sm tracking-[0.4em] uppercase mb-4"
                        style={{ color: '#C5974B', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                    >
                        Festive Collection 2026
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4"
                        style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            color: '#FFFFFF',
                            letterSpacing: '0.06em',
                            lineHeight: 1.15,
                        }}
                    >
                        Dress for Every<br />
                        <span style={{ color: '#C5974B' }}>Celebration</span>
                    </h1>
                    <p
                        className="text-base sm:text-lg font-light mb-10 max-w-md mx-auto"
                        style={{
                            color: 'rgba(255,255,255,0.75)',
                            fontFamily: "'Nunito Sans', sans-serif",
                            lineHeight: 1.6,
                        }}
                    >
                        Handcrafted ethnic wear for weddings, festivals, and every moment worth celebrating.
                    </p>

                    {/* Dual CTAs */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => navigate('/template23/shop?occasion=wedding')}
                            className="px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300 flex items-center justify-center gap-2"
                            style={{
                                backgroundColor: '#C5974B',
                                border: '2px solid #C5974B',
                                color: '#FFFFFF',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 700,
                                borderRadius: '4px',
                                letterSpacing: '1.5px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#D4A85C';
                                e.currentTarget.style.borderColor = '#D4A85C';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#C5974B';
                                e.currentTarget.style.borderColor = '#C5974B';
                            }}
                        >
                            Shop Wedding <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => navigate('/template23/collections')}
                            className="px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300 flex items-center justify-center gap-2"
                            style={{
                                backgroundColor: 'transparent',
                                border: '2px solid #FFFFFF',
                                color: '#FFFFFF',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 700,
                                borderRadius: '4px',
                                letterSpacing: '1.5px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                e.currentTarget.style.color = '#1B3A2D';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                        >
                            Shop Festive
                        </button>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-10 opacity-50" style={{ backgroundColor: '#C5974B' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#C5974B',
                                animation: 'utsavFloat 2s ease-in-out infinite',
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: OCCASION CAROUSEL ===== */}
            <section style={{ backgroundColor: '#FFFDF8', paddingTop: '80px', paddingBottom: '80px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-2"
                            style={{ color: '#C5974B', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                        >
                            Shop by Occasion
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl mb-3"
                            style={{ fontFamily: "'Libre Baskerville', Georgia, serif", color: '#1B3A2D', fontWeight: 700 }}
                        >
                            Find Your Perfect Look
                        </h2>
                        <p
                            className="text-sm sm:text-base max-w-lg mx-auto"
                            style={{ color: '#5A5A5A', fontFamily: "'Nunito Sans', sans-serif", lineHeight: 1.6 }}
                        >
                            From wedding grandeur to festive sparkle, explore curated collections for every Indian celebration.
                        </p>
                    </div>

                    {/* Carousel with Navigation */}
                    <div style={{ position: 'relative' }}>
                        {/* Left Arrow */}
                        <button
                            onClick={() => scrollOccasions('left')}
                            className="utsav-carousel-arrow-left"
                            style={{
                                position: 'absolute',
                                left: '-16px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 10,
                                width: '44px',
                                height: '44px',
                                backgroundColor: '#FFFFFF',
                                border: '2px solid #C5974B',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#C5974B';
                                e.currentTarget.querySelector('svg').style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                e.currentTarget.querySelector('svg').style.color = '#C5974B';
                            }}
                        >
                            <ChevronLeft size={20} style={{ color: '#C5974B', transition: 'color 0.3s ease' }} />
                        </button>

                        {/* Right Arrow */}
                        <button
                            onClick={() => scrollOccasions('right')}
                            className="utsav-carousel-arrow-right"
                            style={{
                                position: 'absolute',
                                right: '-16px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 10,
                                width: '44px',
                                height: '44px',
                                backgroundColor: '#FFFFFF',
                                border: '2px solid #C5974B',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#C5974B';
                                e.currentTarget.querySelector('svg').style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                e.currentTarget.querySelector('svg').style.color = '#C5974B';
                            }}
                        >
                            <ChevronRight size={20} style={{ color: '#C5974B', transition: 'color 0.3s ease' }} />
                        </button>

                        {/* Scrollable Occasions */}
                        <div
                            ref={occasionScrollRef}
                            className="utsav-occasion-scroll"
                            style={{
                                display: 'flex',
                                gap: '24px',
                                overflowX: 'auto',
                                padding: '16px 8px',
                                scrollSnapType: 'x mandatory',
                            }}
                        >
                            {occasions.map((occasion, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate(`/template23/shop?occasion=${occasion.name.toLowerCase()}`)}
                                    style={{
                                        flex: '0 0 auto',
                                        width: '160px',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        scrollSnapAlign: 'start',
                                        transition: 'transform 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-8px)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
                                >
                                    {/* Circle Image */}
                                    <div
                                        style={{
                                            width: '140px',
                                            height: '140px',
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            margin: '0 auto 16px',
                                            border: '3px solid #C5974B',
                                            boxShadow: '0 4px 16px rgba(197, 151, 75, 0.2)',
                                            position: 'relative',
                                        }}
                                    >
                                        <img
                                            src={occasion.image}
                                            alt={occasion.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.5s ease',
                                            }}
                                        />
                                        {/* Subtle Overlay */}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                background: `linear-gradient(to bottom, transparent 50%, ${occasion.color}80 100%)`,
                                                borderRadius: '50%',
                                            }}
                                        />
                                    </div>
                                    {/* Occasion Name */}
                                    <h3
                                        style={{
                                            fontFamily: "'Libre Baskerville', Georgia, serif",
                                            fontSize: '15px',
                                            fontWeight: 700,
                                            color: '#1B3A2D',
                                            margin: '0 0 4px 0',
                                        }}
                                    >
                                        {occasion.name}
                                    </h3>
                                    <p
                                        style={{
                                            fontFamily: "'Nunito Sans', sans-serif",
                                            fontSize: '12px',
                                            color: '#C5974B',
                                            fontWeight: 600,
                                            letterSpacing: '0.5px',
                                            margin: 0,
                                        }}
                                    >
                                        Shop Now &rarr;
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: TRENDING COLLECTION ===== */}
            <section style={{ backgroundColor: '#F8F3EC', paddingTop: '80px', paddingBottom: '80px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-2"
                            style={{ color: '#C5974B', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                        >
                            Curated for You
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl"
                            style={{ fontFamily: "'Libre Baskerville', Georgia, serif", color: '#1B3A2D', fontWeight: 700 }}
                        >
                            This Season&apos;s Edit
                        </h2>
                    </div>

                    {/* Large Collection Cards */}
                    <div className="utsav-trending-grid">
                        {trendingCollections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template23/shop')}
                                className="relative overflow-hidden cursor-pointer group"
                                style={{
                                    borderRadius: '12px',
                                    aspectRatio: index === 0 ? '16/10' : '4/5',
                                }}
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                {/* Gradient Overlay */}
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(27, 58, 45, 0.9) 0%, rgba(27, 58, 45, 0.3) 50%, transparent 100%)',
                                    }}
                                />
                                {/* Occasion Tag */}
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '20px',
                                        left: '20px',
                                        backgroundColor: '#C5974B',
                                        color: '#FFFFFF',
                                        fontFamily: "'Nunito Sans', sans-serif",
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                        padding: '6px 14px',
                                        borderRadius: '20px',
                                    }}
                                >
                                    {collection.occasion}
                                </span>
                                {/* Content */}
                                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                                    <h3
                                        className="text-2xl sm:text-3xl font-bold mb-2"
                                        style={{ fontFamily: "'Libre Baskerville', Georgia, serif", color: '#FFFFFF' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p
                                        className="text-sm mb-4 leading-relaxed max-w-sm"
                                        style={{ color: 'rgba(255,255,255,0.75)', fontFamily: "'Nunito Sans', sans-serif" }}
                                    >
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 transition-all duration-300 group-hover:gap-3"
                                        style={{ color: '#C5974B', fontWeight: 700 }}
                                    >
                                        Explore Collection <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <style>{`
                    .utsav-trending-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 24px;
                    }
                    .utsav-trending-grid > :first-child {
                        grid-column: 1 / -1;
                    }
                    @media (max-width: 768px) {
                        .utsav-trending-grid {
                            grid-template-columns: 1fr;
                        }
                        .utsav-trending-grid > :first-child {
                            grid-column: 1;
                        }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 4: PRODUCT GRID ===== */}
            <section style={{ backgroundColor: '#FFFDF8', paddingTop: '80px', paddingBottom: '80px' }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-2"
                            style={{ color: '#C5974B', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                        >
                            Festive Essentials
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl mb-3"
                            style={{ fontFamily: "'Libre Baskerville', Georgia, serif", color: '#1B3A2D', fontWeight: 700 }}
                        >
                            Our Bestsellers
                        </h2>
                        <p
                            className="text-sm max-w-md mx-auto"
                            style={{ color: '#5A5A5A', fontFamily: "'Nunito Sans', sans-serif" }}
                        >
                            Most loved pieces from our festive and wedding collections.
                        </p>
                    </div>

                    {/* Product Grid */}
                    <div className="utsav-product-grid">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template23/shop')}
                                className="utsav-product-card"
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E8E0D4',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#C5974B';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(197, 151, 75, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#E8E0D4';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                {/* Image */}
                                <div style={{ position: 'relative', aspectRatio: '3/4', overflow: 'hidden' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.6s ease',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                    />
                                    {/* Occasion Badge */}
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: '12px',
                                            left: '12px',
                                            backgroundColor: '#1B3A2D',
                                            color: '#C5974B',
                                            fontFamily: "'Nunito Sans', sans-serif",
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            padding: '5px 12px',
                                            borderRadius: '20px',
                                        }}
                                    >
                                        {product.occasion}
                                    </span>
                                    {/* Wishlist */}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px',
                                            width: '36px',
                                            height: '36px',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            border: 'none',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        <Heart
                                            size={16}
                                            style={{
                                                color: wishlist[product.id] ? '#7A1B3B' : '#5A5A5A',
                                                fill: wishlist[product.id] ? '#7A1B3B' : 'none',
                                                transition: 'all 0.3s ease',
                                            }}
                                        />
                                    </button>
                                </div>

                                {/* Info */}
                                <div style={{ padding: '16px' }}>
                                    <p
                                        style={{
                                            fontFamily: "'Nunito Sans', sans-serif",
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#C5974B',
                                            margin: '0 0 4px 0',
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {product.occasion}
                                    </p>
                                    <h3
                                        style={{
                                            fontFamily: "'Libre Baskerville', Georgia, serif",
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: '#1B3A2D',
                                            margin: '0 0 8px 0',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {product.name}
                                    </h3>
                                    <span
                                        style={{
                                            fontFamily: "'Nunito Sans', sans-serif",
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            color: '#C5974B',
                                        }}
                                    >
                                        {product.price}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* View All Button */}
                    <div className="text-center mt-12">
                        <button
                            onClick={() => navigate('/template23/shop')}
                            className="px-10 py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300 inline-flex items-center gap-2"
                            style={{
                                backgroundColor: '#1B3A2D',
                                color: '#FFFFFF',
                                border: '2px solid #1B3A2D',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 700,
                                borderRadius: '4px',
                                letterSpacing: '1.5px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#1B3A2D';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#1B3A2D';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <style>{`
                    .utsav-product-grid {
                        display: grid;
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                    @media (max-width: 1024px) {
                        .utsav-product-grid {
                            grid-template-columns: repeat(3, 1fr);
                        }
                    }
                    @media (max-width: 768px) {
                        .utsav-product-grid {
                            grid-template-columns: repeat(2, 1fr);
                            gap: 16px;
                        }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 5: CUSTOMIZATION CTA ===== */}
            <section style={{ backgroundColor: '#1B3A2D', padding: '0' }}>
                {/* Gold Line */}
                <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #C5974B, transparent)' }} />

                <div
                    style={{
                        maxWidth: '900px',
                        margin: '0 auto',
                        padding: '60px 24px',
                        textAlign: 'center',
                    }}
                >
                    {/* WhatsApp Icon */}
                    <div
                        style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            border: '2px solid #C5974B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                        }}
                    >
                        <svg
                            width="28"
                            height="28"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#C5974B"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                        </svg>
                    </div>

                    <h2
                        className="text-2xl sm:text-3xl mb-4"
                        style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            color: '#FFFFFF',
                            fontWeight: 700,
                        }}
                    >
                        Need Custom Sizing?
                    </h2>
                    <p
                        className="text-sm sm:text-base mb-3"
                        style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            color: '#C5974B',
                            fontStyle: 'italic',
                        }}
                    >
                        Every body is unique. Every outfit should be too.
                    </p>
                    <p
                        className="text-sm mb-8 max-w-lg mx-auto"
                        style={{
                            color: 'rgba(255,255,255,0.6)',
                            fontFamily: "'Nunito Sans', sans-serif",
                            lineHeight: 1.7,
                        }}
                    >
                        Get custom measurements, fabric choices, and personalized embroidery. Our master tailors ensure a perfect fit for your special occasion.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            className="px-8 py-3.5 text-sm tracking-[0.12em] uppercase transition-all duration-300 inline-flex items-center justify-center gap-2"
                            style={{
                                backgroundColor: '#25D366',
                                border: '2px solid #25D366',
                                color: '#FFFFFF',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 700,
                                borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#20BD5A';
                                e.currentTarget.style.borderColor = '#20BD5A';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#25D366';
                                e.currentTarget.style.borderColor = '#25D366';
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                            WhatsApp Us
                        </button>
                        <button
                            className="px-8 py-3.5 text-sm tracking-[0.12em] uppercase transition-all duration-300 inline-flex items-center justify-center gap-2"
                            style={{
                                backgroundColor: 'transparent',
                                border: '2px solid #C5974B',
                                color: '#C5974B',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontWeight: 700,
                                borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#C5974B';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#C5974B';
                            }}
                        >
                            Book Consultation
                        </button>
                    </div>
                </div>

                <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #C5974B, transparent)' }} />
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            <section style={{ backgroundColor: '#FFFDF8', paddingTop: '80px', paddingBottom: '80px' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 24px' }}>
                    <div className="text-center mb-12">
                        <p
                            className="text-xs tracking-[0.3em] uppercase mb-2"
                            style={{ color: '#C5974B', fontFamily: "'Nunito Sans', sans-serif", fontWeight: 600 }}
                        >
                            Happy Customers
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl"
                            style={{ fontFamily: "'Libre Baskerville', Georgia, serif", color: '#1B3A2D', fontWeight: 700 }}
                        >
                            Celebration Stories
                        </h2>
                    </div>

                    {/* Testimonial Card */}
                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        style={{
                            backgroundColor: '#FFFFFF',
                            border: '2px solid #C5974B',
                            borderRadius: '12px',
                            padding: '40px 32px',
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Decorative Gold Corner */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '60px',
                                height: '60px',
                                borderBottom: '2px solid #C5974B',
                                borderRight: '2px solid #C5974B',
                                opacity: 0.3,
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: '60px',
                                height: '60px',
                                borderTop: '2px solid #C5974B',
                                borderLeft: '2px solid #C5974B',
                                opacity: 0.3,
                            }}
                        />

                        {/* Stars */}
                        <div className="flex justify-center gap-1 mb-6">
                            {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                <Star key={i} size={18} style={{ color: '#C5974B', fill: '#C5974B' }} />
                            ))}
                        </div>

                        {/* Quote */}
                        <p
                            className="text-base sm:text-lg mb-6 leading-relaxed"
                            style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                color: '#1B3A2D',
                                fontStyle: 'italic',
                                maxWidth: '600px',
                                margin: '0 auto 24px',
                            }}
                        >
                            &ldquo;{testimonials[activeTestimonial].text}&rdquo;
                        </p>

                        {/* Author */}
                        <h4
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '15px',
                                fontWeight: 700,
                                color: '#1B3A2D',
                                margin: '0 0 4px 0',
                            }}
                        >
                            {testimonials[activeTestimonial].name}
                        </h4>
                        <p
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '13px',
                                color: '#5A5A5A',
                                margin: '0 0 4px 0',
                            }}
                        >
                            {testimonials[activeTestimonial].city}
                        </p>
                        <p
                            style={{
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '12px',
                                color: '#C5974B',
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                margin: 0,
                            }}
                        >
                            {testimonials[activeTestimonial].occasion}
                        </p>

                        {/* Dots */}
                        <div className="flex justify-center gap-3 mt-8">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    style={{
                                        width: i === activeTestimonial ? '32px' : '10px',
                                        height: '10px',
                                        borderRadius: '5px',
                                        backgroundColor: i === activeTestimonial ? '#C5974B' : '#E8E0D4',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        padding: 0,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            <section style={{ backgroundColor: '#F8F3EC', paddingTop: '64px', paddingBottom: '64px' }}>
                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
                    {/* Decorative Motif */}
                    <div
                        style={{
                            width: '48px',
                            height: '2px',
                            backgroundColor: '#C5974B',
                            margin: '0 auto 24px',
                        }}
                    />

                    <h2
                        className="text-2xl sm:text-3xl mb-3"
                        style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            color: '#1B3A2D',
                            fontWeight: 700,
                        }}
                    >
                        Be First to Know About<br />Festive Collections
                    </h2>
                    <p
                        className="text-sm mb-8"
                        style={{
                            color: '#5A5A5A',
                            fontFamily: "'Nunito Sans', sans-serif",
                            lineHeight: 1.6,
                        }}
                    >
                        Subscribe to get early access to new arrivals, exclusive festive discounts, and styling tips for every Indian occasion.
                    </p>

                    <form
                        onSubmit={handleEmailSubmit}
                        className="utsav-newsletter-form"
                        style={{
                            display: 'flex',
                            gap: '0',
                            maxWidth: '480px',
                            margin: '0 auto',
                        }}
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email address"
                            required
                            style={{
                                flex: 1,
                                padding: '14px 20px',
                                backgroundColor: '#FFFFFF',
                                border: '2px solid #E8E0D4',
                                borderRight: 'none',
                                borderRadius: '4px 0 0 4px',
                                color: '#1B3A2D',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                            }}
                            onFocus={(e) => (e.currentTarget.style.borderColor = '#C5974B')}
                            onBlur={(e) => (e.currentTarget.style.borderColor = '#E8E0D4')}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: '14px 28px',
                                backgroundColor: '#C5974B',
                                color: '#FFFFFF',
                                border: '2px solid #C5974B',
                                borderRadius: '0 4px 4px 0',
                                fontFamily: "'Nunito Sans', sans-serif",
                                fontSize: '13px',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#D4A85C')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C5974B')}
                        >
                            Subscribe
                        </button>
                    </form>

                    <p
                        className="mt-4"
                        style={{
                            fontFamily: "'Nunito Sans', sans-serif",
                            fontSize: '12px',
                            color: '#999',
                        }}
                    >
                        No spam, ever. Unsubscribe anytime.
                    </p>
                </div>

                <style>{`
                    @media (max-width: 480px) {
                        .utsav-newsletter-form {
                            flex-direction: column !important;
                        }
                        .utsav-newsletter-form input {
                            border-right: 2px solid #E8E0D4 !important;
                            border-radius: 4px !important;
                            margin-bottom: 8px;
                        }
                        .utsav-newsletter-form button {
                            border-radius: 4px !important;
                        }
                    }
                `}</style>
            </section>

            {/* Carousel Arrow Responsive */}
            <style>{`
                @media (max-width: 768px) {
                    .utsav-carousel-arrow-left,
                    .utsav-carousel-arrow-right {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Template23;
